import * as Sentry from '@sentry/nextjs';
import { Cause, Match, Schema, SchemaIssue } from 'effect';

/**
 * Base interface for Data.TaggedError types
 */
type TaggedError = {
  readonly _tag: string;
  readonly message?: string;
  readonly [key: string]: unknown;
};

/**
 * Context information for error capture
 */
type CaptureContext = {
  readonly component?: string;
  readonly action?: string;
  readonly additionalData: Record<string, unknown>;
};

/**
 * Extract error tags and metadata from Data.TaggedError
 */
const extractTaggedErrorMetadata = (error: TaggedError) => {
  const { _tag, message, ...restProps } = error;

  return {
    errorTag: _tag,
    errorMessage: message,
    errorData: restProps,
  };
};

const captureParseError = (error: Schema.SchemaError, context: CaptureContext): void => {
  // v4: `SchemaIssue.defaultFormatter` is internal — format the standard-schema
  // issues as `<path>: <message>` lines instead.
  const arrayFormatted = SchemaIssue.makeFormatterStandardSchemaV1()(error.issue).issues;
  const treeFormatted = arrayFormatted
    .map((i) => `${(i.path ?? []).join('.') || '(root)'}: ${i.message}`)
    .join('\n');

  Sentry.captureException(new Error(treeFormatted), {
    level: 'error',
    tags: {
      errorType: 'ParseError',
      component: context.component ?? 'Unknown',
    },
    contexts: {
      parseError: {
        treeFormat: treeFormatted,
        arrayFormat: JSON.stringify(arrayFormatted),
        action: context.action,
        additionalData: context.additionalData,
      },
    },
  });
};

const captureTaggedError = (error: TaggedError, context: CaptureContext): void => {
  const { errorTag, errorMessage, errorData } = extractTaggedErrorMetadata(error);

  const errorInstance = error instanceof Error ? error : new Error(errorMessage ?? errorTag);

  Sentry.captureException(errorInstance, {
    level: 'error',
    tags: {
      errorTag,
      errorType: 'TaggedError',
      component: context.component ?? 'Unknown',
    },
    contexts: {
      taggedError: {
        tag: errorTag,
        message: errorMessage,
        data: errorData,
        action: context.action,
        additionalData: context.additionalData,
      },
    },
  });
};

const captureUnknownError = (error: unknown, context: CaptureContext): void => {
  const errorInstance = error instanceof Error ? error : new Error(JSON.stringify(error, null, 2));

  Sentry.captureException(errorInstance, {
    level: 'error',
    tags: {
      errorType: 'UnknownError',
      component: context.component ?? 'Unknown',
    },
    contexts: {
      unknownError: {
        errorData: error,
        action: context.action,
        additionalData: context.additionalData,
      },
    },
  });
};

export const captureError = (error: unknown, context: CaptureContext): void => {
  if (typeof error === 'object' && error !== null && '_tag' in error) {
    const taggedError = error as { readonly _tag: string };

    Match.value(taggedError).pipe(
      Match.when(
        (e) => Schema.isSchemaError(e),
        (parseError) => {
          captureParseError(parseError, context);
        }
      ),
      Match.orElse((error) => {
        captureTaggedError(error, context);
      })
    );
  } else {
    captureUnknownError(error, context);
  }
};

export const captureCause = <E>(cause: Cause.Cause<E>, context: CaptureContext): void => {
  // Interruption is cancellation, not failure: an unmounted component or an
  // abandoned navigation interrupts its fiber. Only a cause whose reasons are
  // ALL interrupts is dropped, so a failure interrupted mid-flight still reports.
  if (Cause.hasInterruptsOnly(cause)) {
    return;
  }

  const failReason = cause.reasons.find(Cause.isFailReason);
  const dieReason = cause.reasons.find(Cause.isDieReason);
  if (failReason) {
    // ✅ Capture typed errors (Data.TaggedError, ParseError)
    captureError(failReason.error, context);
  } else if (dieReason) {
    // ✅ Capture defects (unexpected errors)
    captureUnknownError(dieReason.defect, {
      ...context,
      action: `${context.action ?? 'Unknown'} (Defect)`,
    });
  } else {
    // ✅ Capture any cause with neither a failure nor a defect left to report
    captureUnknownError(Cause.pretty(cause), {
      ...context,
      action: `${context.action ?? 'Unknown'} (Cause)`,
    });
  }
};
